import { useState, useEffect, useRef } from 'react';
import settings from '../settings';
import { BACKEND_URL } from '../utils';

export function useLobby(socket, playerId) {
  const [lobbyCode, setLobbyCode] = useState('');
  const [hostId, setHostId] = useState('');
  const [players, setPlayers] = useState({});
  const [gameMode, setGameMode] = useState('');
  const [gameState, setGameState] = useState('home');
  const [gameSeed, setGameSeed] = useState(null);
  const [gameKey, setGameKey] = useState(0);
  const [error, setError] = useState('');
  const [startError, setStartError] = useState('');
  const [countdownValue, setCountdownValue] = useState(3);
  const [isRestart, setIsRestart] = useState(false);
  const [pendingInvite, setPendingInvite] = useState(null);
  const [wins, setWins] = useState({});
  const countdownTransitioned = useRef(false);

  // Parse invite from URL on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('invite');
      const name = params.get('name');
      if (code) {
        setPendingInvite({ code: String(code).toUpperCase(), name: name || '' });
      }
    } catch (e) {}
  }, []);

  // When socket ready + pending invite, activate multiplayer UI
  useEffect(() => {
    if (!socket || !pendingInvite) return;
    setGameMode('multiplayer');
    setGameState('home');
  }, [socket, pendingInvite]);

  // Lobby event listeners
  useEffect(() => {
    if (!socket) return;

    const applyGameStart = (data) => {
      setGameSeed(data.game_seed);
      setIsRestart(false);
      setStartError('');
      // If countdown already transitioned to 'playing', skip re-mount
      if (countdownTransitioned.current) {
        countdownTransitioned.current = false;
        return;
      }
      setGameState('playing');
      setGameKey(prev => prev + 1);
    };

    socket.on('lobby_joined', (data) => {
      console.log('Joined lobby:', data);
      setLobbyCode(data.lobby_code);
      setPlayers(data.lobby.players);
      setHostId(data.lobby.host_id);
      setWins(data.lobby.wins || {});
      setGameState('lobby');
    });

    socket.on('player_joined', (data) => {
      console.log('Player joined:', data);
      setPlayers(data.players);
    });

    socket.on('player_left', (data) => {
      console.log('Player left:', data);
      setPlayers(data.players);
      if (data.host_id) setHostId(data.host_id);
    });

    socket.on('countdown_started', (data) => {
      console.log('Countdown started:', data);
      setGameSeed(data.game_seed);
      setGameState('countdown');
      setCountdownValue(data.countdown_duration);
    });

    socket.on('game_started', (data) => {
      console.log('Game started:', data);
      applyGameStart(data);
    });

    socket.on('score_update', (data) => {
      console.log('Score update:', data);
      setPlayers(data.players);
    });

    socket.on('game_ended', (data) => {
      console.log('Game ended:', data);
      setPlayers(data.players);
      if (data.wins) setWins(data.wins);
      setGameState('finished');
    });

    socket.on('game_restarted', (data) => {
      console.log('Game restarted:', data);
      setPlayers(data.lobby.players);
      if (data.lobby.wins) setWins(data.lobby.wins);
      applyGameStart(data);
    });

    socket.on('error', (data) => {
      console.error('Server error:', data.message);
      const msg = data.message || '';
      if (/at least 2 players|required to start|not enough players/i.test(msg)) {
        setStartError(msg);
      } else {
        setError(msg);
      }
    });

    return () => {
      socket.off('lobby_joined');
      socket.off('player_joined');
      socket.off('player_left');
      socket.off('countdown_started');
      socket.off('game_started');
      socket.off('score_update');
      socket.off('game_ended');
      socket.off('game_restarted');
      socket.off('error');
    };
  }, [socket]);

  // Countdown effect
  useEffect(() => {
    if (gameState !== 'countdown') return;

    const playCountdownSound = async () => {
      try {
        const selectedSound = settings.getString(settings.KEYS.SELECTED_SOUND);
        const master = settings.getNumber(settings.KEYS.MASTER);
        const sfx = settings.getNumber(settings.KEYS.SFX);
        if (selectedSound) {
          const vol = (master / 100) * (sfx / 100);
          const AudioManager = await import('../AudioManager');
          await AudioManager.initAudio();
          AudioManager.playSound(selectedSound, { volume: vol });
        }
      } catch (e) {}
    };

    playCountdownSound();

    let hasFinished = false;
    const timer = setInterval(() => {
      setCountdownValue((prev) => {
        const next = prev - 1;
        if (next > 0) {
          playCountdownSound();
          return next;
        } else if (!hasFinished) {
          hasFinished = true;
          if (gameMode === 'multiplayer' && socket && playerId === hostId) {
            socket.emit('countdown_finished', { is_restart: isRestart });
          }
          countdownTransitioned.current = true;
          setGameState('playing');
          setGameKey(prevKey => prevKey + 1);
          setIsRestart(false);
          return 0;
        }
        return 0;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState, socket, playerId, hostId, isRestart, gameMode]);

  // Lobby actions
  const lobbyAction = async (url, name, code) => {
    const action = code ? 'join' : 'create';
    try {
      const response = await fetch(`${BACKEND_URL}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_name: name }),
      });
      const data = await response.json();
      if (data.success) {
        const lobbyCodeValue = code || data.lobby_code;
        setLobbyCode(lobbyCodeValue);
        socket.emit('join_lobby', { lobby_code: lobbyCodeValue, player_name: name });
      } else {
        setError(data.error || `Failed to ${action} lobby`);
      }
    } catch (err) {
      console.error(`Error ${action}ing lobby:`, err);
      setError(`Failed to ${action} lobby`);
    }
  };

  const createLobby = (name) => lobbyAction('/api/lobby/create', name, null);
  const joinLobby = (name, code) => lobbyAction(`/api/lobby/${code}/join`, name, code);

  const startGame = () => {
    setIsRestart(false);
    socket.emit('start_game', {});
  };

  const restartGame = () => {
    setIsRestart(true);
    socket.emit('restart_game', {});
  };

  const acceptInvite = (name, code) => {
    if (!name || !code) return;
    setGameMode('multiplayer');
    joinLobby(name, code);
    setPendingInvite(null);
    try { window.history.replaceState({}, document.title, window.location.pathname); } catch (e) {}
  };

  const declineInvite = () => setPendingInvite(null);

  const handleMultiplayerScore = (score) => {
    if (socket && gameMode === 'multiplayer') {
      socket.emit('point_event', { points: score });
    }
  };

  const resetToMenu = () => {
    // Tell the server the player is leaving so it can clean up the lobby
    // and remove the stale Socket.IO room subscription before we reset
    // local state. Without this the old lobby persists on the server,
    // the socket stays subscribed to the old room, and creating a second
    // lobby can receive stale events that corrupt the new lobby's state.
    if (lobbyCode && socket) {
      try { socket.emit('leave_lobby', {}); } catch (e) {}
    }
    setGameMode('');
    setGameState('home');
    setLobbyCode('');
    setPlayers({});
    setHostId('');
    setGameSeed(null);
    setError('');
    setStartError('');
    setWins({});
  };

  return {
    lobbyCode, hostId, players, wins,
    gameMode, setGameMode,
    gameState, setGameState,
    gameSeed, setGameSeed,
    gameKey, setGameKey,
    error, setError, startError,
    countdownValue, setCountdownValue,
    isRestart,
    pendingInvite,
    createLobby, joinLobby, startGame, restartGame,
    acceptInvite, declineInvite,
    handleMultiplayerScore, resetToMenu,
  };
}
